import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoInformationCircleOutline, IoAlertCircleOutline, IoCheckmarkCircle } from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import { getCommissionSummary } from '../../../services/commissionService';
import { getRealtorProfile } from '../../../services/userProfileService';
import {
  createWithdrawal,
  calcWithdrawalTax,
  WITHDRAWAL_MIN_AMOUNT,
} from '../../../services/withdrawalService';

const Withdraw = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const uid = userData?.uid;

  const [summary, setSummary] = useState({ total: 0, withdrawable: 0 });
  const [account, setAccount] = useState(null); // { accountVerified, accountType, bankName, accountNumber, accountHolder }
  const [loading, setLoading] = useState(true);
  const [amountStr, setAmountStr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [doneId, setDoneId] = useState('');

  useEffect(() => {
    if (!uid) return;
    Promise.all([
      getCommissionSummary(uid).catch(() => ({ total: 0, withdrawable: 0 })),
      getRealtorProfile(uid).catch(() => null),
    ]).then(([s, p]) => {
      setSummary(s || { total: 0, withdrawable: 0 });
      setAccount({
        accountVerified: !!p?.accountVerified,
        accountType: p?.accountType || p?.account_type || 'personal',
        bankName: p?.bankName || p?.bank_name || '',
        accountNumber: p?.accountNumber || p?.account_number || '',
        accountHolder: p?.accountHolder || p?.account_holder || '',
      });
      setLoading(false);
    });
  }, [uid]);

  const amount = Number(String(amountStr).replace(/[^\d]/g, '')) || 0;
  const tax = useMemo(
    () => calcWithdrawalTax(amount, account?.accountType),
    [amount, account?.accountType]
  );

  const canSubmit =
    !!account?.accountVerified &&
    amount >= WITHDRAWAL_MIN_AMOUNT &&
    amount <= summary.withdrawable &&
    !submitting;

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/[^\d]/g, '');
    setAmountStr(raw ? Number(raw).toLocaleString() : '');
    setErrorMsg('');
  };

  const handleMax = () => {
    setAmountStr(summary.withdrawable.toLocaleString());
    setErrorMsg('');
  };

  const handleSubmit = async () => {
    setErrorMsg('');
    if (!account?.accountVerified) {
      setErrorMsg('계좌 인증이 필요합니다.');
      return;
    }
    if (amount < WITHDRAWAL_MIN_AMOUNT) {
      setErrorMsg(`최소 ${WITHDRAWAL_MIN_AMOUNT.toLocaleString()}원 이상부터 출금 가능합니다.`);
      return;
    }
    if (amount > summary.withdrawable) {
      setErrorMsg('출금 가능액을 초과했습니다.');
      return;
    }
    setSubmitting(true);
    try {
      const id = await createWithdrawal(uid, {
        amount,
        bankName: account.bankName,
        accountNumber: account.accountNumber,
        accountHolder: account.accountHolder,
        accountType: account.accountType,
      });
      setDoneId(id);
    } catch (e) {
      setErrorMsg(e?.message || '출금 신청에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Container><InfoText>불러오는 중...</InfoText></Container>;
  }

  // 신청 완료 화면
  if (doneId) {
    return (
      <Container>
        <DoneCard>
          <IoCheckmarkCircle size={48} color="#22C55E" />
          <DoneTitle>출금 신청이 접수되었습니다</DoneTitle>
          <DoneSub>관리자 승인 후 영업일 기준 2~3일 내 입금됩니다.</DoneSub>
        </DoneCard>
        <PrimaryBtn onClick={() => navigate('/realtor/mypage/withdraw-history')}>출금 내역 보기</PrimaryBtn>
        <OutlineBtn onClick={() => navigate('/realtor/mypage')}>마이페이지로</OutlineBtn>
      </Container>
    );
  }

  return (
    <Container>
      {/* 상단 2열 카드 */}
      <TopCardRow>
        <TopCard>
          <TopCardLabel>총 리워드 수익</TopCardLabel>
          <TopCardValue>{summary.total.toLocaleString()}원</TopCardValue>
        </TopCard>
        <TopCard>
          <TopCardLabel>현재 출금가능 수익</TopCardLabel>
          <TopCardValueBlue>{summary.withdrawable.toLocaleString()}원</TopCardValueBlue>
        </TopCard>
      </TopCardRow>

      {/* 계좌 인증 안 된 경우 */}
      {!account?.accountVerified && (
        <>
          <InfoRow>
            <IoInformationCircleOutline size={16} color="#9CA3AF" />
            <InfoText>출금 신청 전 계좌 인증이 필요합니다.</InfoText>
          </InfoRow>

          <SectionLabel>출금 계좌 인증</SectionLabel>
          <StatusText>미인증</StatusText>
          <BlueInfo>계좌 인증 후 출금 신청 버튼이 활성화됩니다. (세법상 규정)</BlueInfo>

          <PrimaryBtn onClick={() => navigate('/realtor/mypage/account')}>계좌 인증하기</PrimaryBtn>
          <OutlineBtn onClick={() => navigate('/realtor/mypage/withdraw-history')}>출금 내역</OutlineBtn>
        </>
      )}

      {/* 계좌 인증 완료 — 출금 신청 폼 */}
      {account?.accountVerified && (
        <>
          {/* 인증 계좌 정보 */}
          <SectionLabel>인증된 출금 계좌</SectionLabel>
          <VerifiedBox>
            <VerifiedRow>
              <IoCheckmarkCircle size={16} color="#22C55E" />
              <span>{account.accountType === 'business' ? '사업자' : '개인'} 계좌</span>
            </VerifiedRow>
            <VerifiedInfoRow>
              <b>{account.bankName || '-'}</b> {account.accountNumber || '-'}
            </VerifiedInfoRow>
            <VerifiedInfoRow>예금주: {account.accountHolder || '-'}</VerifiedInfoRow>
          </VerifiedBox>

          {/* 출금 금액 */}
          <SectionLabel style={{ marginTop: 20 }}>출금 금액</SectionLabel>
          <AmountRow>
            <AmountInput
              type="text"
              inputMode="numeric"
              value={amountStr}
              onChange={handleAmountChange}
              placeholder={`최소 ${WITHDRAWAL_MIN_AMOUNT.toLocaleString()}원`}
            />
            <Unit>원</Unit>
            <MaxBtn type="button" onClick={handleMax}>전액</MaxBtn>
          </AmountRow>
          <HintText>
            최소 {WITHDRAWAL_MIN_AMOUNT.toLocaleString()}원 이상, 최대 {summary.withdrawable.toLocaleString()}원
          </HintText>

          {/* 세금 계산 */}
          {amount > 0 && (
            <CalcBox>
              <CalcRow>
                <CalcLabel>신청 금액</CalcLabel>
                <CalcValue>{amount.toLocaleString()}원</CalcValue>
              </CalcRow>
              <CalcRow>
                <CalcLabel>
                  {tax.taxType === 'vat' ? '부가세 (10%)' : '원천징수 (3.3%)'}
                </CalcLabel>
                <CalcValueMinus>- {tax.taxAmount.toLocaleString()}원</CalcValueMinus>
              </CalcRow>
              <CalcDivider />
              <CalcRow>
                <CalcLabelBold>실 수령액</CalcLabelBold>
                <CalcValueBold>{tax.netAmount.toLocaleString()}원</CalcValueBold>
              </CalcRow>
            </CalcBox>
          )}

          {/* 에러 메시지 */}
          {errorMsg && (
            <ErrorRow>
              <IoAlertCircleOutline size={16} color="#EF4444" />
              <ErrorText>{errorMsg}</ErrorText>
            </ErrorRow>
          )}

          {/* 버튼 */}
          <PrimaryBtn disabled={!canSubmit} onClick={handleSubmit}>
            {submitting ? '처리 중...' : '출금 신청'}
          </PrimaryBtn>
          <OutlineBtn onClick={() => navigate('/realtor/mypage/withdraw-history')}>출금 내역</OutlineBtn>
        </>
      )}
    </Container>
  );
};

export default Withdraw;

const Container = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const TopCardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const TopCard = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const TopCardLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const TopCardValue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const TopCardValueBlue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const InfoText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.4;
`;

const SectionLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const StatusText = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const BlueInfo = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const PrimaryBtn = styled.button`
  width: 100%;
  height: 48px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: ${({ theme }) => theme.spacing.sm};

  &:disabled {
    background: #CBD5E1;
    cursor: not-allowed;
  }
`;

const OutlineBtn = styled.button`
  width: 100%;
  height: 48px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.bgWhite};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
`;

const VerifiedBox = styled.div`
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 14px 16px;
`;

const VerifiedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #166534;
  margin-bottom: 8px;
`;

const VerifiedInfoRow = styled.div`
  font-size: 13px;
  color: #334155;
  line-height: 1.6;

  b {
    font-weight: 600;
    color: #0F172A;
    margin-right: 6px;
  }
`;

const AmountRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 0 12px;
  height: 52px;

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const AmountInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 18px;
  font-weight: 600;
  color: #0F172A;
  text-align: right;

  &::placeholder {
    color: #CBD5E1;
    font-weight: 400;
  }
`;

const Unit = styled.span`
  font-size: 15px;
  color: #64748B;
`;

const MaxBtn = styled.button`
  height: 32px;
  padding: 0 12px;
  background: #EEF2FF;
  color: #4338CA;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #E0E7FF;
  }
`;

const HintText = styled.p`
  font-size: 12px;
  color: #94A3B8;
  margin-top: 6px;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const CalcBox = styled.div`
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 14px 16px;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const CalcRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const CalcLabel = styled.span`
  font-size: 13px;
  color: #64748B;
`;

const CalcValue = styled.span`
  font-size: 14px;
  color: #334155;
  font-weight: 500;
`;

const CalcValueMinus = styled.span`
  font-size: 14px;
  color: #EF4444;
  font-weight: 500;
`;

const CalcDivider = styled.div`
  height: 1px;
  background: #E2E8F0;
  margin: 6px 0;
`;

const CalcLabelBold = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #0F172A;
`;

const CalcValueBold = styled.span`
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 10px 12px;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const ErrorText = styled.p`
  font-size: 13px;
  color: #B91C1C;
  flex: 1;
`;

const DoneCard = styled.div`
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 28px 20px;
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const DoneTitle = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #166534;
  margin: 12px 0 6px;
`;

const DoneSub = styled.p`
  font-size: 13px;
  color: #4B5563;
  line-height: 1.5;
`;
